import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { FormLabel, Tooltip } from "@impero-com/ui";
import { Form, Input } from "antd";
import { Control, Controller, useWatch } from "react-hook-form";
import { z } from "zod";

import { parseFormula } from "../../formulaParser";
import { FieldKey } from "../fields";
import { FormSchema } from "../schema";
import * as theme from "../theme.css";

export const formulaSchema = z
    .string()
    .trim()
    .refine(
        (val) => {
            if (val === "") {
                return true;
            }
            const parseResult = parseFormula(val);
            return parseResult.isOk;
        },
        { error: "The formula is invalid" }
    );

export type Formula = z.infer<typeof formulaSchema>;

const TextArea = Input.TextArea;

interface FieldFormulaProps {
    control: Control<FormSchema>;
    fieldKey: FieldKey;
}

export function FieldFormula({ control, fieldKey }: FieldFormulaProps) {
    const requiredForAssessment = useWatch({
        name: `${fieldKey}.requiredForAssessment`,
        control,
    });

    return (
        <Controller
            control={control}
            name={`${fieldKey}.fieldType.formula`}
            render={({ field, fieldState }) => {
                const hasValue = field.value && field.value !== "";
                const disableTextArea = !hasValue && requiredForAssessment;
                const errors = fieldState.invalid
                    ? [
                          <p className={theme.errorText}>
                              {fieldState.error?.message}
                          </p>,
                      ]
                    : [];
                const textArea = (
                    <TextArea
                        placeholder="Formula"
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        status={errors.length > 0 ? "error" : undefined}
                        className={fieldState.invalid ? ERRORED_CLASSNAME : ""}
                        readOnly={disableTextArea}
                    />
                );
                return (
                    <>
                        <FormLabel>Formula</FormLabel>
                        {disableTextArea ? (
                            <Tooltip title="A field required for assessment cannot have a formula">
                                {textArea}
                            </Tooltip>
                        ) : (
                            textArea
                        )}
                        <Form.ErrorList errors={errors} />
                    </>
                );
            }}
        />
    );
}
